import { getSeatSelectionBookingById } from '@/features/purchase/purchaseActions';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import {
  fetchFloorPlanIcons,
  fetchPlaceAvailability,
  updatePlaceSelections,
} from '@/features/purchase/purchaseService';
import { useDispatch, useSelector } from '@/store/utils';
import type {
  AvailabilitiesPlaceMapDTO,
  FloorPlanIconsDTO,
} from '@/types/dto/agent';
import { getBookingAdmissions, getLegDirection } from '@/utils/booking';
import { GTM_EVENT, trackCustomEvent } from '@/utils/gtm';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

export interface SeatSelectionContextValue {
  selectedLegId?: string;
  selectedReservationId?: string;
  selectedCarriageIndex: number;
  selectedFloorIndex: number;
  isLoading: boolean;
  isUndoing: boolean;
  availabilitiesPlaceMap?: AvailabilitiesPlaceMapDTO;
  floorPlanIcons: FloorPlanIconsDTO | undefined;
  currentPlaceMap:
    | NonNullable<AvailabilitiesPlaceMapDTO['placeMaps']>[number]
    | null;
  seatSelectionSuccess: boolean;
  seatSelectionError: boolean;
  setSelectedCarriageIndex: (index: number) => void;
  setSelectedFloorIndex: (index: number) => void;
  setIsUndoing: (open: boolean) => void;
  handleLegSelect: (legId: string) => Promise<void>;
  handleSeatSelect: (placeId: string) => void;
  handleReservationSelect: (reservationId: string) => void;
  handlePassengerSeatClick: (placeId: string, reservationId: string) => void;
  closeModal: () => void;
  refreshPlaceAvailability: () => Promise<void>;
}

export const SeatSelectionContext = createContext<
  SeatSelectionContextValue | undefined
>(undefined);

interface SeatSelectionProviderProps {
  children: ReactNode;
  bookingId: string;
}

export const SeatSelectionProvider = ({
  children,
  bookingId,
}: SeatSelectionProviderProps) => {
  const booking = useSelector(purchaseFlowBookingSelector);
  const dispatch = useDispatch();
  const [selectedLegId, setSelectedLegId] = useState<string>();
  const [selectedReservationId, setSelectedReservationId] = useState<string>();
  const [selectedCarriageIndex, setSelectedCarriageIndex] = useState(0);
  const [selectedFloorIndex, setSelectedFloorIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [seatSelectionSuccess, setSeatSelectionSuccess] = useState(false);
  const [seatSelectionError, setSeatSelectionError] = useState(false);
  const [availabilitiesPlaceMap, setAvailabilitiesPlaceMap] =
    useState<AvailabilitiesPlaceMapDTO>();
  const [floorPlanIcons, setFloorPlanIcons] = useState<FloorPlanIconsDTO>();
  const [isUndoing, setIsUndoing] = useState(false);

  useEffect(() => {
    fetchFloorPlanIcons().then((icons) => setFloorPlanIcons(icons));
  }, []);

  const currentPlaceMap = useMemo(() => {
    const maps = availabilitiesPlaceMap?.placeMaps;

    return maps ? maps[selectedCarriageIndex] : null;
  }, [availabilitiesPlaceMap?.placeMaps, selectedCarriageIndex]);

  const findSelectedPlace = useCallback(
    (placeId: string) =>
      currentPlaceMap?.floors?.[selectedFloorIndex]?.places?.find(
        (place) => place.id === placeId
      ),
    [currentPlaceMap, selectedFloorIndex]
  );

  const findCoveredLegId = useCallback(
    (
      admission: ReturnType<typeof getBookingAdmissions>[number],
      tripId: string
    ) => {
      if (!admission.coveredLegIds?.length) return undefined;

      if (selectedLegId) {
        const exactMatch = admission.coveredLegIds.find(
          (legId: string) => legId && legId === selectedLegId
        );

        if (exactMatch) {
          return exactMatch;
        }
      }

      return admission.coveredLegIds.find(
        (legId: string) => legId && legId.startsWith(tripId)
      );
    },
    [selectedLegId]
  );

  const getSeatSelectionData = useCallback(
    (
      selectedPlace: NonNullable<
        NonNullable<
          NonNullable<AvailabilitiesPlaceMapDTO['placeMaps']>[number]['floors']
        >[number]['places']
      >[number]
    ) => {
      if (!booking || !selectedReservationId || !selectedLegId) return null;

      const admissions = getBookingAdmissions(booking);
      const currentAdmission = admissions.find((admission) =>
        admission.reservations?.some((r) => r.id === selectedReservationId)
      );

      if (!currentAdmission) {
        console.error('Admission not found for the selected reservation');
        return null;
      }

      const currentPassenger = booking.passengers?.find(
        (passenger) =>
          passenger.id && currentAdmission?.passengerIds?.includes(passenger.id)
      );

      if (!currentPassenger?.externalReference) {
        console.error('Passenger not found or missing external reference');
        return null;
      }

      const currentReservation = currentAdmission.reservations?.find(
        (r) => r.id === selectedReservationId
      );

      if (!currentReservation) {
        console.error('Reservation not found');
        return null;
      }

      const trip = booking.bookedTrips?.find((trip) =>
        trip.legs?.some((leg) => leg.id === selectedLegId)
      );

      if (!trip?.id) {
        console.error('Trip not found');
        return null;
      }

      const coveredLegId = findCoveredLegId(currentAdmission, trip.id);

      if (!coveredLegId) {
        console.error('Covered leg ID not found');
        return null;
      }

      return {
        reservationId: selectedReservationId,
        places: [
          {
            placeNumber: selectedPlace.number,
            coachNumber: currentPlaceMap?.number,
            passengerRef: currentPassenger.externalReference,
          },
        ],
        tripLegCoverage: {
          legId: coveredLegId,
          tripId: trip.id,
        },
      };
    },
    [
      booking,
      selectedReservationId,
      selectedLegId,
      currentPlaceMap,
      findCoveredLegId,
    ]
  );

  const refreshPlaceAvailability = useCallback(async () => {
    if (!bookingId || !selectedReservationId) return;

    const newPlaceMap = await fetchPlaceAvailability(
      bookingId,
      selectedReservationId
    );

    setAvailabilitiesPlaceMap(newPlaceMap);
  }, [bookingId, selectedReservationId]);

  const handleLegSelect = useCallback(
    async (legId: string) => {
      if (!bookingId) return;

      setSelectedLegId(legId);

      if (booking) {
        setIsLoading(true);

        try {
          const admissions = getBookingAdmissions(booking);
          const legAdmissions = admissions.filter((admission) =>
            admission.coveredLegIds?.includes(legId)
          );

          if (legAdmissions.length > 0) {
            const firstAdmission = legAdmissions[0];
            const firstReservation = firstAdmission.reservations?.[0];

            if (firstReservation?.id) {
              const newReservationId = firstReservation.id;

              setSelectedReservationId(newReservationId);

              const newPlaceMap = await fetchPlaceAvailability(
                bookingId,
                newReservationId
              );

              setSelectedCarriageIndex(0);
              setSelectedFloorIndex(0);
              setAvailabilitiesPlaceMap(newPlaceMap);
            } else {
              setSelectedReservationId(undefined);
              setAvailabilitiesPlaceMap(undefined);
            }
          } else {
            setSelectedReservationId(undefined);
            setAvailabilitiesPlaceMap(undefined);
          }
        } catch (error) {
          console.error('Failed to update seat selection for new leg:', error);
          setAvailabilitiesPlaceMap(undefined);
        } finally {
          setIsLoading(false);
        }

        return;
      }

      if (!selectedReservationId) return;

      setIsLoading(true);

      try {
        const newPlaceMap = await fetchPlaceAvailability(
          bookingId,
          selectedReservationId
        );

        setSelectedCarriageIndex(0);
        setSelectedFloorIndex(0);
        setAvailabilitiesPlaceMap(newPlaceMap);
      } catch (error) {
        console.error('Failed to fetch place availability:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [bookingId, selectedReservationId, booking]
  );

  const handleSeatSelect = useCallback(
    async (placeId: string) => {
      if (!bookingId || !selectedLegId || !selectedReservationId || !booking) {
        console.error('Missing required parameters for seat selection', {
          bookingId,
          selectedLegId,
          selectedReservationId,
        });

        return;
      }

      setIsLoading(true);
      setSeatSelectionSuccess(false);
      setSeatSelectionError(false);

      try {
        const selectedPlace = findSelectedPlace(placeId);

        if (!selectedPlace) {
          console.error('Selected place not found');
          return;
        }

        const seatSelectionData = getSeatSelectionData(selectedPlace);

        if (!seatSelectionData) {
          console.error('Could not retrieve required data for seat selection');
          return;
        }

        await updatePlaceSelections(bookingId, [seatSelectionData]);
        await dispatch(getSeatSelectionBookingById(bookingId)).unwrap();
        await refreshPlaceAvailability();

        trackCustomEvent(GTM_EVENT.SEAT_SELECTED, {
          direction: getLegDirection(booking, selectedLegId),
          legId: selectedLegId,
          passengerRef: seatSelectionData.places[0].passengerRef,
          coachNumber: currentPlaceMap?.number,
          placeNumber: selectedPlace?.number,
        });
        setSeatSelectionSuccess(true);
      } catch (error) {
        console.error('Error selecting seat:', error);
        setSeatSelectionError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [
      bookingId,
      selectedLegId,
      selectedReservationId,
      booking,
      findSelectedPlace,
      getSeatSelectionData,
      refreshPlaceAvailability,
      dispatch,
    ]
  );

  const handleReservationSelect = useCallback(
    async (reservationId: string) => {
      if (!bookingId) return;

      setSelectedReservationId(reservationId);
      setIsLoading(true);

      let effectiveLegId = selectedLegId;

      try {
        let openedPassengerRef: string | undefined;

        if (booking) {
          const admissions = getBookingAdmissions(booking);
          const admission = admissions.find((admission) =>
            admission.reservations?.some((r) => r.id === reservationId)
          );

          openedPassengerRef = booking.passengers?.find(
            (p) => p.id && admission?.passengerIds?.includes(p.id)
          )?.externalReference;

          if (admission?.coveredLegIds?.length) {
            if (selectedLegId) {
              const legIsValid =
                admission.coveredLegIds.includes(selectedLegId);

              if (!legIsValid) {
                effectiveLegId = admission.coveredLegIds[0];
                setSelectedLegId(effectiveLegId);
              }
            } else {
              effectiveLegId = admission.coveredLegIds[0];
              setSelectedLegId(effectiveLegId);
            }
          }
        }

        const newPlaceMap = await fetchPlaceAvailability(
          bookingId,
          reservationId
        );

        const reservedSeat = booking?.bookedTrips
          ?.flatMap((trip) => trip.bookedOffers ?? [])
          ?.flatMap((offer) => offer.admissions ?? [])
          ?.flatMap((admission) => admission.reservations ?? [])
          ?.find((r) => r.id === reservationId)?.placeAllocations
          ?.reservedPlaces?.[0];

        if (booking) {
          if (reservedSeat && newPlaceMap?.placeMaps?.length) {
            const carriageIndex = newPlaceMap.placeMaps.findIndex(
              (coach) => coach.number === reservedSeat.coachNumber
            );

            setSelectedCarriageIndex(carriageIndex >= 0 ? carriageIndex : 0);

            const coach =
              newPlaceMap.placeMaps[carriageIndex >= 0 ? carriageIndex : 0];

            if (coach?.floors?.length) {
              for (let i = 0; i < coach.floors.length; i++) {
                const floor = coach.floors[i];
                const isOnThisFloor = floor.places?.some(
                  (place) => place.number === reservedSeat.placeNumber?.[0]
                );

                if (isOnThisFloor) {
                  setSelectedFloorIndex(i);
                  break;
                }
              }
            }
          } else {
            setSelectedFloorIndex(0);
          }
        } else {
          setSelectedFloorIndex(0);
        }

        setAvailabilitiesPlaceMap(newPlaceMap);
        trackCustomEvent(GTM_EVENT.SEAT_SELECTION_OPENED, {
          direction: getLegDirection(booking, effectiveLegId),
          legId: effectiveLegId,
          passengerRef: openedPassengerRef,
          currentCoachNumber: reservedSeat?.coachNumber,
          currentPlaceNumber: reservedSeat?.placeNumber?.[0],
        });
      } catch (error) {
        console.error('Failed to fetch place availability:', error);
        setAvailabilitiesPlaceMap(undefined);
      } finally {
        setIsLoading(false);
      }
    },
    [bookingId, booking, selectedLegId]
  );

  const handlePassengerSeatClick = useCallback(
    async (placeId: string, reservationId: string) => {
      if (reservationId === selectedReservationId) {
        handleSeatSelect(placeId);

        return;
      }

      setIsLoading(true);

      try {
        const currentLegId = selectedLegId;

        await handleReservationSelect(reservationId);

        if (currentLegId && booking) {
          const admissions = getBookingAdmissions(booking);
          const admission = admissions.find((a) =>
            a.reservations?.some((r) => r.id === reservationId)
          );

          if (admission?.coveredLegIds?.includes(currentLegId)) {
            setSelectedLegId(currentLegId);
          }
        }
      } catch (error) {
        console.error('Error switching passenger seat:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      selectedReservationId,
      selectedLegId,
      booking,
      handleSeatSelect,
      handleReservationSelect,
    ]
  );

  const closeModal = useCallback(() => {
    if (!isLoading && !isUndoing) {
      setSelectedReservationId(undefined);
      setAvailabilitiesPlaceMap(undefined);
      setSelectedLegId(undefined);
      setSeatSelectionSuccess(false);
      setSeatSelectionError(false);
    }
  }, [isLoading, isUndoing]);

  const value = useMemo<SeatSelectionContextValue>(
    () => ({
      selectedLegId,
      selectedReservationId,
      selectedCarriageIndex,
      selectedFloorIndex,
      isLoading,
      isUndoing,
      availabilitiesPlaceMap,
      floorPlanIcons,
      currentPlaceMap,
      seatSelectionSuccess,
      seatSelectionError,
      setSelectedCarriageIndex,
      setSelectedFloorIndex,
      setIsUndoing,
      handleLegSelect,
      handleSeatSelect,
      handleReservationSelect,
      handlePassengerSeatClick,
      closeModal,
      refreshPlaceAvailability,
    }),
    [
      selectedLegId,
      selectedReservationId,
      selectedCarriageIndex,
      selectedFloorIndex,
      isLoading,
      isUndoing,
      availabilitiesPlaceMap,
      floorPlanIcons,
      currentPlaceMap,
      seatSelectionSuccess,
      seatSelectionError,
      handleLegSelect,
      handleSeatSelect,
      handleReservationSelect,
      handlePassengerSeatClick,
      closeModal,
      refreshPlaceAvailability,
    ]
  );

  return (
    <SeatSelectionContext.Provider value={value}>
      {children}
    </SeatSelectionContext.Provider>
  );
};
